import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

// ============================================================================
// SECTIONED MODAL SHELL — shared by the front-office workspaces
// (Quick Check-In and Walk-In), Aug 2026 redesign.
// ============================================================================
// One shell, two screens: a left section nav with per-section status badges,
// a persistent summary strip that keeps the decision-critical state (balance,
// rooms assigned, dates) visible whatever section is open, and a footer that
// names what blocks completion. Sections switch freely — this is deliberately
// NOT a wizard: desk work is non-linear, and a forced order would fight the
// operator. All section content stays MOUNTED (hidden with display:none) so
// switching never refetches or loses in-flight component state.

export type ShellBadge = 'ok' | 'warn' | { done: number; total: number };

export interface ShellSection {
  id: string;
  title: string;
  /** Completion signal: '✓' / '⚠' / 'n / m'. Hidden while errorCount > 0. */
  badge?: ShellBadge;
  /** Validation problems anchored to this section — renders a red count badge
   *  that takes precedence over `badge`, so the operator can see from the nav
   *  which section the submit button is complaining about. */
  errorCount?: number;
  content: React.ReactNode;
}

/** One entry of the footer blocker list — clicking jumps to the section. */
export interface ShellBlocker {
  label: string;
  sectionId: string;
}

interface SectionedModalShellProps {
  title: React.ReactNode;
  /** Extra header content next to the title (e.g. the GRC number chip). */
  headerExtra?: React.ReactNode;
  /** Persistent strip under the header — identity + live status chips. */
  summary?: React.ReactNode;
  /** Full-width banners above the section body (special notes, status guards)
   *  — visible whatever section is active. */
  banners?: React.ReactNode;
  sections: ShellSection[];
  activeSection: string;
  onSectionChange: (id: string) => void;
  /** Footer content — blockers + action buttons, composed by the caller. */
  footer: React.ReactNode;
  onClose: () => void;
  /** Renders the minimize-to-strip button when provided. */
  onMinimize?: () => void;
  /** TRUE while submitting — disables close/minimize and backdrop dismissal. */
  busy?: boolean;
  loading?: { active: boolean; label: string };
  /** Defaults to 1280px — this shell hosts the heaviest screens in the app. */
  maxWidth?: string;
}

/** Summary-strip status pill. Shared so both screens speak one language. */
export const ShellChip: React.FC<{
  tone: 'ok' | 'warn' | 'info';
  children: React.ReactNode;
  title?: string;
}> = ({ tone, children, title }) => (
  <span className={`smodal-chip ${tone}`} title={title}>{children}</span>
);

/** Footer blocker list — each entry jumps to the offending section. */
export const ShellBlockers: React.FC<{
  blockers: ShellBlocker[];
  onJump: (sectionId: string) => void;
}> = ({ blockers, onJump }) => {
  if (blockers.length === 0) return <span style={{ marginRight: 'auto' }} />;
  return (
    <span className="chk-blockers" style={{ marginRight: 'auto' }}>
      {blockers.map((b, i) => (
        <React.Fragment key={`${b.sectionId}-${b.label}`}>
          {i > 0 && ' · '}
          <button
            type="button"
            className="smodal-blocker"
            onClick={() => onJump(b.sectionId)}
            title="Go to this section"
          >
            {b.label}
          </button>
        </React.Fragment>
      ))}
    </span>
  );
};

const badgeFor = (s: ShellSection): { text: string; cls: string } | null => {
  if (s.errorCount && s.errorCount > 0) {
    return { text: `⚠ ${s.errorCount}`, cls: 'err' };
  }
  if (s.badge === 'ok') return { text: '✓', cls: 'done' };
  if (s.badge === 'warn') return { text: '⚠', cls: 'part' };
  if (s.badge) {
    return {
      text: `${s.badge.done} / ${s.badge.total}`,
      cls: s.badge.done >= s.badge.total ? 'done' : 'part',
    };
  }
  return null;
};

const SectionedModalShell: React.FC<SectionedModalShellProps> = ({
  title, headerExtra, summary, banners, sections, activeSection,
  onSectionChange, footer, onClose, onMinimize, busy = false, loading,
  maxWidth = '1280px',
}) => (
  <>
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
    <div
      className="modal-container modal-xl"
      style={{ maxWidth, minHeight: 'auto', height: 'min(85vh, 780px)' }}
    >
      {loading?.active && (
        <div style={{
          position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
        }}>
          <div style={{
            width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
            borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
            {loading.label}
          </span>
        </div>
      )}

      <div className="modal-header" style={{ minHeight: 'auto' }}>
        <h2 className="modal-title">{title}</h2>
        {headerExtra}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginLeft: 'auto' }}>
          {onMinimize && (
            <button className="modal-header-btn" onClick={onMinimize} title="Minimize to bar" disabled={busy}>
              <IconSystem.chevronDown size={16} />
            </button>
          )}
          <button className="modal-close" onClick={onClose} disabled={busy}>
            <IconSystem.x size={20} />
          </button>
        </div>
      </div>

      {summary && <div className="smodal-summary">{summary}</div>}

      <div className="smodal-body">
        <nav className="smodal-nav">
          {sections.map((s, idx) => {
            const badge = badgeFor(s);
            return (
              <button
                key={s.id}
                type="button"
                className={`smodal-nav-item${s.id === activeSection ? ' active' : ''}`}
                onClick={() => onSectionChange(s.id)}
              >
                <span className="smodal-nav-label">
                  <span className="smodal-nav-num">{idx + 1}</span>
                  {s.title}
                </span>
                {badge && <span className={`smodal-nav-badge ${badge.cls}`}>{badge.text}</span>}
              </button>
            );
          })}
        </nav>
        <div className="smodal-content">
          {banners}
          {sections.map((s) => (
            <div
              key={s.id}
              style={{ display: s.id === activeSection ? 'block' : 'none' }}
            >
              {s.content}
            </div>
          ))}
        </div>
      </div>

      <div className="modal-footer" style={{ minHeight: 'auto' }}>{footer}</div>
    </div>
  </>
);

export default SectionedModalShell;
